/**
 * Montar o diagrama — o desenho em duas colunas, arrastando.
 *
 * **É o `Editar` do Sistema de hoje.** Ali as duas colunas são
 * `connectedSortable` do jQuery UI e cada bloco é o desenho daquele componente;
 * ao soltar, o `sortable_confirmar` manda os ids na ordem em que ficaram. Aqui é
 * o mesmo gesto com arrastar nativo, sem biblioteca, e a mesma rota de gravação.
 *
 * **O que é barato e o que não é.** Reordenar dentro da coluna não muda o traço
 * de componente nenhum — muda a pilha, e a tela já tem os desenhos. Trocar de
 * lado muda: o motor espelha o componente (`passos.Right()`), então só o que
 * atravessou é rebuscado, um por um. O diagrama inteiro custa 8 s e só é
 * carregado ao abrir.
 *
 * **Grava ao soltar**, como o legado. O traço publicado não se mexe: quem o
 * reescreve é o Redesenhar.
 *
 * **Os escondidos ficam à parte.** Fusível, sinal e aterramento que já aparecem
 * dentro da ligação de outro componente não têm desenho próprio — o motor os
 * omite. A tela os lista abaixo, como o legado faz com os `nao_exibidos`, em vez
 * de mostrar caixa vazia.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  desenhoDoBloco, usarDesagruparPorGrupos, usarDesagruparTudo, usarGravarMontagem,
  usarMontagem, type BlocoDaMontagem, type LigacaoParaDesagrupar,
} from "@/api/montagemDoDiagrama"
import { usarDiagrama } from "@/api/diagramas"
import { usarAgrupar, usarConferirAgrupamento } from "@/api/desenhoDoDiagrama"
import {
  pinosRepetidos, usarTerminaisDoComponente, type TerminalDoComponente,
} from "@/api/terminaisDoComponente"
import { Secao } from "@/kit/Secao"
import { BarraLateral } from "@/kit/LarguraArrastavel"
import { Link } from "react-router-dom"
import {
  comCor, fichaDe, usarComponentesDoDiagrama, usarEditarComponenteDoDiagrama, usarEditarCoresEmLote,
  usarExcluirDoDiagrama, usarRemoverPngDoComponente,
  type ComponenteDoDiagrama, type FichaDoComponente,
} from "@/api/desenhoDoDiagrama"
import {
  usarAgruparLigacoes, usarCandidatosDoComponente, usarIncluirLigacoesDoComponente,
  usarTransformarInterno, type CandidatoDoComponente,
} from "@/api/ferramentasDoDiagrama"
import { chaveDoPar, type LinhaMarcada, type PinoCandidato } from "@/api/ligacoesEmLote"
import { FerramentasDoDiagrama } from "./FerramentasDoDiagrama"
import { TerminaisDoComponente } from "./TerminaisDoComponente"
import { SeletorDeCorDeFuncao } from "./SeletorDeCorDeFuncao"
import { SeletorDeDesenhoInterno } from "./SeletorDeDesenhoInterno"
import { SeletorDeTipo } from "./TipoDoComponente"
import { Rotulado } from "./Rotulado"
import { higienizarSvg } from "@/kit/svgSeguro"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { emOrdemDePinoPrincipal, NomeDoCandidato, PinosDoCandidato } from "./CandidatoDeLigacao"

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível gravar."
}

/** O par de ícones do bloco — o `glyphicon-move` e o `glyphicon-cog` do legado. */
const iconeDoBloco: React.CSSProperties = {
  width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
  borderRadius: 4, fontSize: 12, lineHeight: 1,
  background: "rgba(255,255,255,0.86)", color: "#4B5563", border: "1px solid #D1D5DB",
}

const botao: React.CSSProperties = {
  padding: "4px 9px", fontSize: 11.5, border: "1px solid var(--line)",
  borderRadius: 5, background: "var(--surface)", color: "var(--txt)", cursor: "pointer",
}

/**
 * De que lado cai um bloco solto na área de fora das colunas: o do clique em
 * relação ao centro do componente principal. Em cima do centro vai para a direita,
 * como o desenho do legado (a esquerda é o que fica antes do principal).
 */
export function ladoDaAreaDeFora(clientX: number, centroDoPrincipal: number): "left" | "right" {
  return clientX < centroDoPrincipal ? "left" : "right"
}

/**
 * O realce do lado de destino durante o arraste. Mistura do token de ação do tema
 * com transparente: a área do diagrama é sempre branca (o desenho é preto sobre
 * branco), então um `--acao-soft` opaco do tema escuro taparia o traço, e o tom
 * com alfa tinge nos dois temas sem tapar.
 */
const REALCE_DO_LADO = "color-mix(in srgb, var(--acao) 20%, transparent)"

export function MontarDiagrama() {
  const { projetoId: p, diagramaId: d } = useParams()
  const projetoId = Number(p)
  const diagramaId = Number(d)
  const navegar = useNavigate()

  const montagem = usarMontagem(diagramaId)
  const gravar = usarGravarMontagem(projetoId, diagramaId)
  // A ficha e os terminais pedem campos que a montagem nao carrega (cor, desenho
  // interno, altura); vem da listagem, que e barata e ja fica em cache.
  const fichas = usarComponentesDoDiagrama(diagramaId)
  // O sistema do diagrama e o que escolhe os posicionamentos padrao.
  const diagrama = usarDiagrama(diagramaId)
  const sistemaDoDiagrama = diagrama.data?.sistemaId ?? 0

  const [blocos, setBlocos] = useState<BlocoDaMontagem[]>([])
  const [zoom, setZoom] = useState(1)
  // Quanto cada coluna entra por cima do principal. Quem manda é o motor: a API
  // devolve a medida em px do desenho, e o zoom a acompanha.
  const sobreposicao = (montagem.data?.sobreposicaoPx ?? 0) * zoom
  const [recado, setRecado] = useState<string | null>(null)
  const [arrastando, setArrastando] = useState<number | null>(null)
  const [alvo, setAlvo] = useState<{ lado: string; antes: number | null } | null>(null)
  const [comandos, setComandos] = useState<number | null>(null)
  const [marcados, setMarcados] = useState<number[]>([])
  const [ferramentas, setFerramentas] = useState(false)
  const [ajuda, setAjuda] = useState(false)
  const quadro = useRef<HTMLElement>(null)
  const area = useRef<HTMLDivElement>(null)
  const principal = useRef<HTMLDivElement>(null)
  const telaCheia = usarTelaCheia(quadro)

  useEffect(() => { if (montagem.data) setBlocos(montagem.data.blocos) }, [montagem.data])

  /**
   * Marca e desmarca. **A seleção não atravessa a coluna** — é a regra do
   * legado: o `selectable` de lá limpa o outro lado assim que você seleciona
   * neste. Agrupar componentes de colunas diferentes não faz sentido, porque o
   * agrupado fica de um lado só.
   */
  function marcar(id: number, lado: string) {
    setMarcados((atuais) => {
      if (atuais.includes(id)) return atuais.filter((x) => x !== id)
      const mesmoLado = atuais.filter((x) =>
        blocos.find((b) => b.diagramaComponenteId === x)?.posicao === lado)
      return [...mesmoLado, id]
    })
  }

  const esquerda = useMemo(
    () => blocos.filter((b) => b.posicao === "left" && b.exibir), [blocos])
  const direita = useMemo(
    () => blocos.filter((b) => b.posicao === "right" && b.exibir), [blocos])
  const escondidos = useMemo(() => blocos.filter((b) => !b.exibir), [blocos])

  /**
   * Zoom que faz o diagrama inteiro caber na largura da área. A soma é a do
   * próprio desenho: o bloco mais largo de cada coluna mais o principal, menos o
   * que as colunas entram por cima dele.
   */
  function caberNaLargura() {
    const m = montagem.data
    const largura = area.current?.clientWidth ?? 0
    if (!m || largura <= 0) return
    const maior = (bs: BlocoDaMontagem[]) => Math.max(0, ...bs.map((b) => b.larguraPx))
    const natural = maior(esquerda) + maior(direita) + m.principalLarguraPx - 2 * m.sobreposicaoPx
    if (natural <= 0) return
    const z = Math.floor(((largura - 24) / natural) * 10) / 10
    setZoom(Math.min(2, Math.max(0.4, z)))
  }

  /**
   * Solta o bloco: reordena na memória e grava. Só quem **trocou de lado** pede
   * desenho novo — o traço de quem só mudou de lugar na pilha é o mesmo.
   */
  async function soltar(lado: string, antes: number | null) {
    const id = arrastando
    setArrastando(null); setAlvo(null)
    if (id == null) return

    const bloco = blocos.find((b) => b.diagramaComponenteId === id)
    if (!bloco) return

    const trocouDeLado = bloco.posicao !== lado
    const sem = blocos.filter((b) => b.diagramaComponenteId !== id)
    const movido = { ...bloco, posicao: lado }

    // Reconstrói a coluna alvo com o bloco na posição em que foi solto.
    const daColuna = sem.filter((b) => b.posicao === lado && b.exibir)
    const i = antes == null
      ? daColuna.length
      : daColuna.findIndex((b) => b.diagramaComponenteId === antes)
    const nova = [...daColuna]
    nova.splice(i < 0 ? nova.length : i, 0, movido)

    const resto = sem.filter((b) => !(b.posicao === lado && b.exibir))
    const juntos = [...resto, ...nova]
    setBlocos(juntos)

    const ids = (l: string) =>
      juntos.filter((b) => b.posicao === l && b.exibir).map((b) => b.diagramaComponenteId)

    setRecado(null)
    gravar.mutate({ left: ids("left"), right: ids("right") }, {
      onSuccess: async () => {
        if (!trocouDeLado) {
          setRecado("Ordem gravada.")
          return
        }
        setRecado("Trocou de lado — redesenhando este componente…")
        try {
          const novo = await desenhoDoBloco(id)
          setBlocos((bs) => bs.map((b) => b.diagramaComponenteId === id
            ? { ...b, svg: novo.svg, larguraPx: novo.svgLarguraPx, alturaPx: novo.svgAlturaPx }
            : b))
          setRecado("Gravado, e o componente foi redesenhado do outro lado.")
        } catch (e) {
          setRecado("Gravado, mas o desenho novo não veio: " + mensagem(e))
        }
      },
      onError: (e) => {
        setBlocos(montagem.data?.blocos ?? [])
        setRecado(mensagem(e))
      },
    })
  }

  /**
   * O lado da área de fora, pelo clientX contra o centro do principal. Com uma
   * coluna só (`posicao` left/right = o principal fica nesse lado), só há um lado
   * para onde soltar.
   */
  function ladoDeFora(clientX: number): string {
    const so = montagem.data?.posicao
    if (so === "left") return "right"
    if (so === "right") return "left"
    const r = principal.current?.getBoundingClientRect()
    return ladoDaAreaDeFora(clientX, r ? r.left + r.width / 2 : clientX)
  }

  const coluna = (lado: string, itens: BlocoDaMontagem[]) => (
    <div
      // Qualquer altura da coluna (ela estica até o fim do diagrama) aceita soltar:
      // fora de um bloco cai no fim. A propagação para aqui, senão a área de fora
      // também trataria o mesmo drop.
      onDragOver={(ev) => { ev.preventDefault(); ev.stopPropagation(); setAlvo({ lado, antes: null }) }}
      onDrop={(ev) => {
        ev.preventDefault(); ev.stopPropagation()
        soltar(lado, alvo?.lado === lado ? alvo.antes : null)
      }}
      style={{
        // Fundo branco na area de edicao inteira: o desenho e preto sobre branco,
        // e a moldura escura do tema em volta de cada bloco fazia o contorno que
        // ficou horrivel. No legado o diagrama tambem vive sobre branco.
        //
        // `flex: 0 0 auto` e o `float` do legado: a coluna encolhe para o desenho
        // mais largo em vez de ocupar metade da tela. E o alinhamento encosta os
        // blocos no componente principal — `float:right` na esquerda,
        // `float:left` na direita —, que e o que faz os pinos de todos os
        // componentes cairem na mesma linha vertical.
        flex: "0 0 auto", display: "flex", flexDirection: "column", gap: 0,
        alignItems: lado === "left" ? "flex-end" : "flex-start",
        padding: 0, minHeight: 120, minWidth: 48,
        // O contêiner estica as colunas (`alignItems: stretch`); o lado curto vira
        // alvo da altura toda, não só da caixa do conteúdo.
        alignSelf: "stretch",
        position: "relative", zIndex: 1,
        // Sem fundo proprio, pelo mesmo motivo do bloco: a coluna avanca 34 px sobre a
        // barra do principal, e branco opaco aqui apagaria o cinza na faixa em que os
        // pinos precisam atravessar. O realce de "solte aqui" fica com alfa, para
        // tingir sem tapar.
        background: alvo?.lado === lado ? REALCE_DO_LADO : "transparent",
        boxShadow: alvo?.lado === lado ? "inset 0 0 0 2px var(--acao)" : "none",
      }}
    >
      <span style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase",
                     letterSpacing: 0.4, marginBottom: 4, padding: "0 2px",
                     alignSelf: lado === "left" ? "flex-start" : "flex-end" }}>
        {lado === "left" ? "esquerda" : "direita"} · {itens.length}
      </span>

      {itens.map((b) => (
        <div
          key={b.diagramaComponenteId}
          onDragOver={(ev) => {
            ev.preventDefault(); ev.stopPropagation()
            setAlvo({ lado, antes: b.diagramaComponenteId })
          }}
          onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); soltar(lado, b.diagramaComponenteId) }}
          onClick={(ev) => {
            // Como no legado, clicar no traço seleciona; os ícones não (lá é o
            // `cancel: '.icons, rect, g.aterramento, ellipse'`).
            if ((ev.target as HTMLElement).closest("[data-icone]")) return
            marcar(b.diagramaComponenteId, lado)
          }}
          style={{
            position: "relative", cursor: "pointer",
            // A largura e a do proprio desenho, como no legado. Com `100%` cada
            // traco esticava ate a borda da coluna, cada um numa escala diferente,
            // e os pinos paravam de se alinhar com a barra do meio.
            width: b.larguraPx > 0 ? b.larguraPx * zoom : undefined,
            maxWidth: "100%",
            // Fundo nenhum, como `.DivDiagramaComponente` no legado. Branco opaco aqui
            // cobriria a barra do principal justamente na faixa em que o bloco entra
            // por cima dela — o desenho apagaria o que precisa atravessar. O branco e
            // da area do diagrama, uma vez so.
            background: marcados.includes(b.diagramaComponenteId)
              ? "rgba(37,99,235,0.14)" : "transparent",
            opacity: arrastando === b.diagramaComponenteId ? 0.4 : 1,
            // A marca de onde vai cair e sombra, nao borda: borda empurraria o
            // bloco 2 px para baixo e a coluna inteira dancaria durante o arraste.
            boxShadow: alvo?.lado === lado && alvo.antes === b.diagramaComponenteId
              ? "inset 0 2px 0 0 #2563EB" : "none",
          }}
        >
          {/* Os ícones do legado são `position: absolute` numa caixa de 32 px
              (`.icons`, Site.css 554): não ocupam linha, ficam sobre a margem
              vazia do próprio desenho. Era o que separava os blocos aqui — cada
              um ganhava uma faixa de ícones acima do traço.

              Os dois vão na borda de fora da coluna, com o punho sempre como o
              mais externo, na mesma hierarquia do sistema de hoje. */}
          <div style={{
            position: "absolute", top: 0, zIndex: 3,
            ...(lado === "right" ? { right: 0 } : { left: 0 }),
            display: "flex", flexDirection: lado === "right" ? "row-reverse" : "row",
            gap: 2, padding: 1,
          }}>
            <span
              draggable
              onDragStart={() => setArrastando(b.diagramaComponenteId)}
              onDragEnd={() => { setArrastando(null); setAlvo(null) }}
              title="Mover"
              data-icone="mover"
              style={{ ...iconeDoBloco, cursor: "grab" }}
            >⠿</span>
            <span
              onClick={() => { setComandos(b.diagramaComponenteId); setFerramentas(false) }}
              title="Configurações"
              data-icone="config"
              style={{ ...iconeDoBloco, cursor: "pointer" }}
            >⚙</span>
          </div>

          <Traco svg={b.svg} largura={b.larguraPx} zoom={zoom} rotulo={`${b.codigo} ${b.descricao}`} />
        </div>
      ))}

      {itens.length === 0 && (
        <span style={{ fontSize: 11, color: "var(--txt3)", padding: 8 }}>
          Nenhum componente deste lado. Arraste um para cá.
        </span>
      )}
    </div>
  )

  return (
    // `quadro` é o que vai para a tela cheia: sem o menu, a faixa do projeto e as
    // abas por cima, o diagrama fica com a tela toda (relatório de 28/09/2026).
    <section ref={quadro}
             style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0,
                      background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px",
                    borderBottom: "1px solid var(--line2)", flex: "0 0 auto", flexWrap: "wrap" }}>
        {!telaCheia.ligada && (
          <button onClick={() => navegar(`/projetos/${projetoId}/diagramas`)} style={botao}>
            ← Diagramas
          </button>
        )}
        <b style={{ fontSize: 13 }}>Montar o diagrama {diagramaId}</b>
        <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                        alignItems: "center", gap: 6 }}>
          zoom
          <input type="range" min={0.4} max={2} step={0.1} value={zoom}
                 onChange={(e) => setZoom(Number(e.target.value))} style={{ width: 90 }} />
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{zoom.toFixed(1)}×</span>
        </label>
        <button onClick={caberNaLargura} disabled={!montagem.data} style={botao}
                title="Ajusta o zoom para o diagrama inteiro caber na largura">
          Caber na largura
        </button>
        {telaCheia.disponivel && (
          <button onClick={telaCheia.alternar} style={{ ...botao, fontWeight: 600 }}
                  title={telaCheia.ligada ? "Volta ao sistema (Esc)" : "Esconde o menu, a faixa e as abas"}>
            {telaCheia.ligada ? "Sair da tela cheia" : "Tela cheia"}
          </button>
        )}
        {/* O aviso que era uma faixa fixa, e roubava altura do desenho, agora abre no "?". */}
        <button onClick={() => setAjuda((a) => !a)} aria-expanded={ajuda}
                aria-label="Como funciona a montagem" title="Como funciona a montagem"
                style={{ ...botao, width: 26, padding: 0, fontWeight: 700,
                         background: ajuda ? "var(--acao-soft)" : botao.background }}>
          ?
        </button>
        <button onClick={() => { setFerramentas((f) => !f); setComandos(null) }}
                aria-pressed={ferramentas}
                style={{ ...botao, marginLeft: "auto", fontWeight: 600,
                         background: ferramentas ? "var(--acao-soft)" : botao.background }}>
          Ferramentas
        </button>
        <button onClick={() => montagem.refetch()} disabled={montagem.isFetching}
                style={botao}>
          {montagem.isFetching ? "Carregando…" : "Recarregar"}
        </button>
      </div>

      {/* Abaixo do cabeçalho: o desenho com as faixas dele, e ao lado a barra
          lateral encaixada. As faixas vão da ponta até a barra, sem passar por
          baixo dela; o cabeçalho fica sempre à vista. */}
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {ajuda && (
        <div className="barra" style={{
          flex: "0 0 auto", padding: "7px 14px", fontSize: 11.5, lineHeight: 1.5,
          background: "var(--acao-soft)", color: "var(--acao-on-soft)",
          borderBottom: "1px solid var(--acao)",
        }}>
          <b>Arraste para reordenar ou trocar de lado.</b>{" "}
          <span style={{ opacity: 0.9 }}>
            Grava ao soltar. Mudar de lugar na coluna não redesenha nada; trocar de lado redesenha
            só o componente que atravessou, porque o traço dele espelha. Clique no traço para
            marcar (cor e agrupar em lote); a engrenagem abre o componente. O desenho que o
            cliente vê só muda no <b>Redesenhar</b>.
          </span>
        </div>
      )}

      {marcados.length > 0 && (
        <BarraDeSelecao
          projetoId={projetoId}
          diagramaId={diagramaId}
          marcados={marcados}
          blocos={blocos}
          aoLimpar={() => setMarcados([])}
          aoMudar={(texto) => { setRecado(texto); setMarcados([]); montagem.refetch() }}
        />
      )}

      {recado && (
        <div style={{ flex: "0 0 auto", padding: "7px 14px", fontSize: 11.5,
                      background: "var(--surface2)", borderBottom: "1px solid var(--line2)" }}>
          {recado}
        </div>
      )}

      {montagem.isPending ? (
        <p style={{ padding: 16, fontSize: 12, color: "var(--txt3)" }}>
          Carregando o diagrama inteiro — de 1 a 10 s, conforme o tamanho…
        </p>
      ) : montagem.error ? (
        <p style={{ padding: 16, fontSize: 12, color: "var(--erro)" }}>{mensagem(montagem.error)}</p>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}>
        {montagem.isFetching && <Recarregando />}
        {/* A área branca de fora das colunas também aceita soltar: o lado sai do clientX
            contra o centro do principal e o bloco vai para o fim dele. Colunas e blocos
            param a propagação, então um drop nunca chega aqui duas vezes. */}
        <div ref={area}
             onDragOver={(ev) => {
               if (arrastando == null) return
               ev.preventDefault(); setAlvo({ lado: ladoDeFora(ev.clientX), antes: null })
             }}
             onDrop={(ev) => {
               if (arrastando == null) return
               ev.preventDefault(); soltar(ladoDeFora(ev.clientX), null)
             }}
             style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "auto", padding: 6,
                      background: "#FFFFFF" }}>
          {/* O <style> do diagrama, uma vez, alcançando todos os blocos. */}
          {montagem.data && (
            <style dangerouslySetInnerHTML={{ __html: CSS_DOS_BLOCOS + estiloDeDentro(montagem.data.estilos) }} />
          )}

          {/* Sem vão entre as colunas: no legado as três são `float:left` coladas, e
              é o que faz a ligação sair do pino e entrar no principal sem um
              pedaço de branco no meio. */}
          <div style={{ display: "flex", gap: 0, alignItems: "stretch",
                        width: "fit-content", minWidth: "100%" }}>
            {montagem.data?.posicao !== "left" && coluna("left", esquerda)}

            {/* As colunas entram POR CIMA do principal — é o que põe os pinos dentro da
                caixa do meio, como no desenho de verdade. A medida vem da API
                (`sobreposicaoPx`, os 6 passos do `componente_principal_margem`), e o
                `zIndex` repete o que o legado faz com `center: 1` e colunas `1000`. */}
            <div ref={principal}
                 style={{ flex: "0 0 auto", alignSelf: "stretch", display: "flex",
                          flexDirection: "column", alignItems: "center", gap: 4,
                          minWidth: 24, position: "relative", zIndex: 0,
                          marginLeft: -sobreposicao, marginRight: -sobreposicao }}>
              <span style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase",
                             letterSpacing: 0.4 }}>principal</span>
              <div style={{ background: "#FFFFFF", flex: 1, display: "flex",
                            alignItems: "stretch", overflow: "hidden" }}>
                <Traco svg={montagem.data?.principalSvg ?? ""}
                       largura={montagem.data?.principalLarguraPx ?? 0}
                       zoom={zoom} rotulo="Componente principal" esticar />
              </div>
            </div>

            {montagem.data?.posicao !== "right" && coluna("right", direita)}
          </div>

          {escondidos.length > 0 && (
            <div style={{ marginTop: 14, padding: "9px 11px", borderRadius: 8,
                          background: "#F3F4F6", color: "#374151" }}>
              <b style={{ fontSize: 11.5 }}>
                {escondidos.length} sem desenho próprio
              </b>
              <p style={{ margin: "3px 0 7px", fontSize: 10.5, color: "#6B7280", lineHeight: 1.5 }}>
                Fusível, sinal e aterramento que já aparecem dentro da ligação de outro componente.
                O motor não os desenha em separado — no Sistema de hoje eles ficam na mesma lista à
                parte.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {escondidos.map((b) => (
                  <span key={b.diagramaComponenteId}
                        style={{ fontSize: 10.5, padding: "3px 7px", borderRadius: 5,
                                 background: "#FFFFFF", color: "#374151",
                                 border: "1px solid #E5E7EB" }}>
                    {b.codigo} <span style={{ color: "#9CA3AF" }}>{b.posicao}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <p style={{ marginTop: 10, fontSize: 10, color: "#9CA3AF" }}>
            {blocos.length} componentes · carregado em{" "}
            {((montagem.data?.milissegundos ?? 0) / 1000).toFixed(1)} s
          </p>
        </div>
        </div>
      )}
      </div>

      {ferramentas && (
        <FerramentasDoDiagrama
          projetoId={projetoId}
          diagramaId={diagramaId}
          sistemaId={(fichas.data ?? []).length > 0 ? sistemaDoDiagrama : 0}
          componentes={blocos.map((b) => ({
            id: b.diagramaComponenteId, codigo: b.codigo, descricao: b.descricao,
          }))}
          aoFechar={() => setFerramentas(false)}
          aoMudar={(texto) => { setRecado(texto); montagem.refetch() }}
        />
      )}

      {comandos != null && (
        <PainelDeComandos
          key={comandos}
          projetoId={projetoId}
          diagramaId={diagramaId}
          componente={(fichas.data ?? []).find((c) => c.id === comandos) ?? null}
          bloco={blocos.find((b) => b.diagramaComponenteId === comandos) ?? null}
          blocos={blocos}
          marcados={marcados}
          aoFechar={() => setComandos(null)}
          aoMudar={(texto) => { setRecado(texto); setMarcados([]); montagem.refetch() }}
        />
      )}
      </div>
    </section>
  )
}

/**
 * A barra que aparece com componentes marcados — o que o legado faz com o
 * `selectable` mais o item "Agrupar" do menu.
 *
 * **Agrupar junta caixas do mesmo componente numa só**: as ligações dos
 * marcados passam para o primeiro, e as caixas que ficam vazias somem. Nenhum
 * pino em uso se perde. Como as caixas somem de verdade (não vão para a
 * lixeira), confere antes e diz o que vai acontecer.
 *
 * **Só com o mesmo componente**, como no legado, que só mostrava o "Agrupar"
 * nesse caso. A API recusa o resto.
 *
 * **O primeiro marcado é quem recebe**, como no legado, onde o componente do
 * menu é o alvo e os demais vão para dentro dele.
 */
function BarraDeSelecao({ projetoId, diagramaId, marcados, blocos, aoLimpar, aoMudar }: {
  projetoId: number
  diagramaId: number
  marcados: number[]
  blocos: BlocoDaMontagem[]
  aoLimpar: () => void
  aoMudar: (recado: string) => void
}) {
  const [conferindo, setConferindo] = useState(false)
  // O primeiro marcado recebe a fusao, como no legado.
  const principal = marcados[0] ?? 0
  const mesmoComponente = new Set(
    marcados.map((id) => blocos.find((b) => b.diagramaComponenteId === id)?.componenteId)).size === 1
  // A cor em lote, que morava na lista de componentes do painel: aqui ela se
  // aplica ao que se vê marcado no desenho.
  const cores = usarEditarCoresEmLote(projetoId, diagramaId)
  const [pintando, setPintando] = useState(false)
  const [cor, setCor] = useState("")

  const nomes = marcados
    .map((id) => blocos.find((b) => b.diagramaComponenteId === id)?.codigo ?? String(id))
    .join(", ")

  return (
    <div style={{
      flex: "0 0 auto", padding: "8px 14px", fontSize: 11.5, lineHeight: 1.5,
      background: "var(--acao-soft)", color: "var(--acao-on-soft)",
      borderBottom: "1px solid var(--acao)",
      display: "flex", flexDirection: "column", gap: 7,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <b>{marcados.length} marcado{marcados.length > 1 ? "s" : ""}:</b>
        <span style={{ opacity: 0.9 }}>{nomes}</span>
        {marcados.length > 1 && (
          <span style={{ opacity: 0.75 }}>
            — o primeiro ({blocos.find((b) => b.diagramaComponenteId === principal)?.codigo}) recebe
          </span>
        )}
        <button onClick={aoLimpar} style={{ ...botao, marginLeft: "auto" }}>Limpar</button>
        {!pintando && !conferindo && (
          <button onClick={() => setPintando(true)} style={botao}>Cor…</button>
        )}
        {marcados.length > 1 && !conferindo && !pintando && (
          <button onClick={() => setConferindo(true)} disabled={!mesmoComponente}
                  title={mesmoComponente
                    ? "Junta as caixas marcadas numa só, a primeira"
                    : "Só junta caixas do mesmo componente"}
                  style={{ ...botao, fontWeight: 600, opacity: mesmoComponente ? 1 : 0.5,
                           cursor: mesmoComponente ? "pointer" : "not-allowed" }}>
            Agrupar…
          </button>
        )}
      </div>

      {marcados.length > 1 && !mesmoComponente && (
        <span style={{ fontSize: 11, opacity: 0.85 }}>
          Agrupar junta caixas do <b>mesmo componente</b> — a seleção tem componentes diferentes.
        </span>
      )}

      {pintando && (
        <div style={{ background: "var(--surface)", color: "var(--txt)", borderRadius: 7,
                      padding: "9px 11px", display: "flex", flexDirection: "column", gap: 8 }}>
          <b style={{ fontSize: 11.5 }}>Cor de função para {marcados.length === 1 ? "o marcado" : `os ${marcados.length} marcados`}</b>
          <SeletorDeCorDeFuncao valor={cor} aoMudar={setCor} rotulo="Cor dos marcados" />
          <div style={{ display: "flex", gap: 7 }}>
            <button
              onClick={() => cores.mutate(
                marcados.map((id) => ({ diagramaComponenteId: id, cor })),
                {
                  onSuccess: (r) => { setPintando(false); aoMudar(`Cor aplicada em ${r.alterados} componente(s).`) },
                  onError: (e) => aoMudar(mensagem(e)),
                })}
              disabled={cores.isPending}
              style={{ ...botao, fontWeight: 600 }}>
              {cores.isPending ? "Aplicando…" : "Aplicar"}
            </button>
            <button onClick={() => setPintando(false)} style={botao}>Cancelar</button>
          </div>
        </div>
      )}

      {conferindo && (
        <ConfirmarAgrupamento
          projetoId={projetoId} diagramaId={diagramaId}
          principal={principal} ids={marcados}
          aoMudar={(texto) => { setConferindo(false); aoMudar(texto) }}
          aoCancelar={() => setConferindo(false)}
        />
      )}
    </div>
  )
}

/**
 * Confere e junta — o passo final do agrupar, da faixa de seleção e do painel
 * da engrenagem.
 *
 * `ids` vai com **todas** as caixas, o principal junto, e com vírgula, como o
 * legado: a rotina dele só funde quando acha vírgula na string.
 */
function ConfirmarAgrupamento({ projetoId, diagramaId, principal, ids, aoMudar, aoCancelar }: {
  projetoId: number
  diagramaId: number
  principal: number
  ids: number[]
  aoMudar: (recado: string) => void
  aoCancelar: () => void
}) {
  const selecionados = ids.join(",")
  const conferencia = usarConferirAgrupamento(principal, selecionados, true)
  const agrupar = usarAgrupar(projetoId, diagramaId)
  const somem = conferencia.data?.somem.length ?? 0

  return (
    <div style={{ background: "var(--surface)", color: "var(--txt)", borderRadius: 7,
                  border: "1px solid var(--line)",
                  padding: "9px 11px", display: "flex", flexDirection: "column", gap: 7 }}>
      {conferencia.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Conferindo…</span>
      ) : conferencia.error ? (
        <>
          <span style={{ fontSize: 11.5, color: "var(--erro)" }}>{mensagem(conferencia.error)}</span>
          <button onClick={aoCancelar} style={{ ...botao, alignSelf: "flex-start" }}>Voltar</button>
        </>
      ) : (
        <>
          <b style={{ fontSize: 11.5 }}>
            {conferencia.data?.totalQueMuda ?? 0} ligação(ões) passam para{" "}
            {conferencia.data?.principal}, e {somem} caixa(s) vazia(s) somem.
          </b>
          <span style={{ fontSize: 11, color: "var(--txt2)", lineHeight: 1.5 }}>
            Nenhum pino em uso se perde. As caixas que somem não vão para a lixeira — não tem
            restaurar.
            {(conferencia.data?.totalNaLixeira ?? 0) > 0 && (
              <> <b>{conferencia.data?.totalNaLixeira} ligação(ões) que já estavam na lixeira
                dessas caixas somem junto.</b></>
            )}
          </span>
          <div style={{ display: "flex", gap: 7 }}>
            <button
              onClick={() => agrupar.mutate(
                { principalId: principal, selecionados },
                {
                  onSuccess: () => aoMudar(`Agrupado: ${somem + 1} caixas viraram uma.`),
                  onError: (e) => aoMudar(mensagem(e)),
                })}
              disabled={agrupar.isPending || somem === 0}
              style={{ ...botao, fontWeight: 600 }}>
              {agrupar.isPending ? "Agrupando…" : "Agrupar"}
            </button>
            <button onClick={aoCancelar} style={botao}>Cancelar</button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * O desagrupar de um componente — o modal `Desagrupar` do Sistema de hoje.
 *
 * Lá as ligações são caixas que você arrasta entre grupos; aqui cada ligação
 * escolhe o grupo num seletor, que dá no mesmo e é mais rápido de operar. O
 * **grupo 1 fica no componente atual** e cada grupo seguinte ganha um componente
 * novo — é o que o `Finalizar` do legado faz.
 *
 * **"Desagrupar tudo"** é o outro botão do modal: uma ligação por componente,
 * sem montar grupo nenhum.
 */
function Desagrupar({ projetoId, diagramaId, componenteId, codigo, aoMudar }: {
  projetoId: number
  diagramaId: number
  componenteId: number
  codigo: string
  aoMudar: (recado: string) => void
}) {
  const terminais = usarTerminaisDoComponente(componenteId)
  const tudo = usarDesagruparTudo(projetoId, diagramaId)
  const porGrupos = usarDesagruparPorGrupos(projetoId, diagramaId)
  const [grupoDe, setGrupoDe] = useState<Record<number, number>>({})

  const linhas = terminais.data ?? []
  const grupos = useMemo(() => {
    const max = Math.max(1, ...Object.values(grupoDe))
    const saida: LigacaoParaDesagrupar[][] = Array.from({ length: max }, () => [])
    for (const t of linhas) {
      const g = (grupoDe[t.id] ?? 1) - 1
      saida[g]?.push({
        id: t.id, pinoutId: t.pinoutId, pino: t.pino,
        circuitoDescricao: "", posicao: t.posicao, pinoutInternoId: 0,
      })
    }
    return saida
  }, [linhas, grupoDe])

  const entrada: React.CSSProperties = {
    padding: "3px 6px", fontSize: 11, border: "1px solid var(--line)",
    borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
  }

  if (terminais.isPending) {
    return <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando as ligações…</p>
  }
  if (linhas.length <= 1) {
    return (
      <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>
        {codigo} tem {linhas.length} ligação — não há o que desagrupar.
      </p>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <span style={{ fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
        O grupo 1 fica neste componente; cada grupo a mais vira um componente novo, cópia deste.
        Nada é apagado — as ligações mudam de dono.
      </span>

      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {linhas.map((t) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8,
                                   padding: "4px 7px", borderRadius: 6,
                                   background: "var(--surface2)", fontSize: 11.5 }}>
            <b style={{ minWidth: 54 }}>pino {t.pino || "—"}</b>
            <span style={{ color: "var(--txt3)", flex: 1, minWidth: 0, overflow: "hidden",
                           textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.codigoDoOutro} {t.descricaoDoOutro}
            </span>
            <span style={{ color: "var(--txt3)", fontSize: 10.5 }}>
              {t.posicao === "ligacao" ? "lado do componente" : "lado oposto"}
            </span>
            <select
              value={grupoDe[t.id] ?? 1}
              onChange={(e) => setGrupoDe({ ...grupoDe, [t.id]: Number(e.target.value) })}
              style={{ ...entrada, width: 76 }}>
              {Array.from({ length: Math.min(linhas.length, 8) }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>grupo {n}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        <button
          onClick={() => porGrupos.mutate({ id: componenteId, grupos }, {
            onSuccess: () => aoMudar(
              `Desagrupado em ${grupos.filter((g) => g.length > 0).length} grupo(s).`),
            onError: (e) => aoMudar(mensagem(e)),
          })}
          disabled={porGrupos.isPending || grupos.filter((g) => g.length > 0).length < 2}
          style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
          {porGrupos.isPending ? "Separando…" : "Finalizar em grupos"}
        </button>

        <button
          onClick={() => tudo.mutate(componenteId, {
            onSuccess: (r) => aoMudar(
              `Desagrupado tudo — ${r.componentesCriados} componente(s) novo(s).`),
            onError: (e) => aoMudar(mensagem(e)),
          })}
          disabled={tudo.isPending}
          style={{ ...entrada, cursor: "pointer" }}>
          {tudo.isPending ? "Desagrupando…" : "Desagrupar tudo"}
        </button>
      </div>
    </div>
  )
}

/**
 * O painel de comandos do bloco — o `DiagramaComandos` do Sistema de hoje.
 *
 * **O menu do legado tem 18 itens** e é o centro da edição. Aqui cada assunto é
 * um título com setinha que abre e fecha, para o painel caber na tela fechado e
 * só buscar o que alguém abriu.
 *
 * **Juntar caixas vem primeiro**, quando há outra caixa do mesmo componente na
 * coluna. No legado o menu da engrenagem sabia da seleção e oferecia o
 * "Agrupar"; aqui a engrenagem abria só a caixa clicada, e quem procurava juntar
 * caía no "Agrupar ligações", que apaga pino repetido — outra coisa.
 *
 * **Remover do diagrama é o último**, fechado, para não ser clicado de passagem.
 */
function PainelDeComandos({ projetoId, diagramaId, componente, bloco, blocos, marcados, aoFechar, aoMudar }: {
  projetoId: number
  diagramaId: number
  componente: ComponenteDoDiagrama | null
  bloco: BlocoDaMontagem | null
  blocos: BlocoDaMontagem[]
  marcados: number[]
  aoFechar: () => void
  aoMudar: (recado: string) => void
}) {
  const salvar = usarEditarComponenteDoDiagrama(projetoId, diagramaId)
  const excluir = usarExcluirDoDiagrama(projetoId, diagramaId)
  const tirarPng = usarRemoverPngDoComponente(projetoId, diagramaId)
  const [ficha, setFicha] = useState<FichaDoComponente | null>(
    componente ? fichaDe(componente) : null)
  // Barato e em cache: é o que decide se "Remover pino repetido" aparece.
  const terminais = usarTerminaisDoComponente(componente?.id ?? 0)
  const repetidos = useMemo(() => pinosRepetidos(terminais.data ?? []), [terminais.data])

  useEffect(() => { if (componente) setFicha(fichaDe(componente)) }, [componente])

  const entrada: React.CSSProperties = {
    padding: "4px 7px", fontSize: 11.5, border: "1px solid var(--line)",
    borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
  }
  const campo = (rotulo: string, filho: React.ReactNode) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10, color: "var(--txt3)", textTransform: "uppercase",
                     letterSpacing: 0.4 }}>{rotulo}</span>
      {filho}
    </label>
  )
  const fechando = (texto: string) => { aoMudar(texto); aoFechar() }

  return (
    <BarraLateral chave="painel-do-componente" padrao={520} rotulo="Componente do diagrama">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <b style={{ fontSize: 13 }}>
            {bloco ? bloco.codigo + " " + bloco.descricao : "Componente"}
          </b>
          <button onClick={aoFechar} style={{ ...entrada, marginLeft: "auto", cursor: "pointer" }}>
            fechar
          </button>
        </div>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>
          {bloco?.posicao === "left" ? "coluna esquerda" : "coluna direita"} · ordem {bloco?.ordem}
          {" · "}{bloco?.terminais ?? componente?.terminais ?? 0} ligações
        </span>

        {componente == null ? (
          <p style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando a ficha…</p>
        ) : (
          <>
            {bloco && (
              <JuntarCaixas
                projetoId={projetoId} diagramaId={diagramaId}
                bloco={bloco} blocos={blocos} marcados={marcados}
                aoMudar={fechando}
              />
            )}

            <Link to={"/projetos/" + projetoId + "/diagramas/" + diagramaId + "/componentes/" + componente.id}
                  style={{ ...entrada, textDecoration: "none", display: "block" }}>
              <b>Editor gráfico do componente →</b>{" "}
              <span style={{ color: "var(--txt3)" }}>
                o traço com grade, as medidas em passos e cada ligação
              </span>
            </Link>

            <div>
              <Secao titulo="Editar no diagrama" chave="componente-ficha" aberta>
                {ficha && (
                  <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "flex-end" }}>
                    {campo("Título", (
                      <input value={ficha.titulo}
                             onChange={(e) => setFicha({ ...ficha, titulo: e.target.value })}
                             style={{ ...entrada, width: 150 }} />
                    ))}
                    {campo("Texto", (
                      <input value={ficha.texto}
                             onChange={(e) => setFicha({ ...ficha, texto: e.target.value })}
                             style={{ ...entrada, width: 150 }} />
                    ))}
                    {campo("Tipo", (
                      <SeletorDeTipo valor={ficha.tipo} estilo={{ ...entrada, width: 190 }}
                                     aoMudar={(t) => setFicha({ ...ficha, tipo: t })} />
                    ))}
                    {campo("Altura", (
                      <input type="number" value={ficha.altura}
                             onChange={(e) => setFicha({ ...ficha, altura: Number(e.target.value) })}
                             style={{ ...entrada, width: 58 }} />
                    ))}
                    <label style={{ fontSize: 10.5, color: "var(--txt2)", display: "flex",
                                    alignItems: "center", gap: 5 }}>
                      <input type="checkbox" checked={ficha.ocultar}
                             onChange={(e) => setFicha({ ...ficha, ocultar: e.target.checked })} />
                      ocultar
                    </label>
                    <div style={{ flexBasis: "100%" }}>
                      <Rotulado rotulo="Cor">
                        <SeletorDeCorDeFuncao valor={ficha.componenteCor}
                                              aoMudar={(c) => setFicha(comCor(ficha, c, bloco?.posicao ?? componente.posicao))} />
                      </Rotulado>
                    </div>
                    <div style={{ flexBasis: "100%" }}>
                      <Rotulado rotulo="Desenho interno">
                        <SeletorDeDesenhoInterno
                          posicao={bloco?.posicao ?? componente.posicao}
                          valor={ficha.diagramaDesenhoInternoId}
                          aoMudar={(id) => setFicha({ ...ficha, diagramaDesenhoInternoId: id })} />
                      </Rotulado>
                    </div>
                    {ficha.diagramaDesenhoInternoId > 0 && (
                      <label style={{ flexBasis: "100%", fontSize: 10.5, color: "var(--txt2)", display: "flex",
                                      alignItems: "center", gap: 5 }}
                             title="Marca deslocar em todas as ligações do componente. Não desmarca nada.">
                        <input type="checkbox" checked={ficha.deslocar}
                               onChange={(e) => setFicha({ ...ficha, deslocar: e.target.checked })} />
                        deslocar as ligações para o desenho interno
                      </label>
                    )}
                    <button
                      onClick={() => salvar.mutate({ id: componente.id, ficha }, {
                        onSuccess: () => aoMudar("Componente gravado — o traço foi refeito."),
                        onError: (e) => aoMudar(mensagem(e)),
                      })}
                      disabled={salvar.isPending}
                      style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
                      {salvar.isPending ? "Gravando…" : "Gravar"}
                    </button>
                  </div>
                )}
                {componente.png && (
                  <button
                    onClick={() => tirarPng.mutate(componente.id, {
                      onSuccess: () => aoMudar(`Imagem de fundo de ${bloco?.codigo ?? "componente"} removida.`),
                      onError: (e) => aoMudar(mensagem(e)),
                    })}
                    disabled={tirarPng.isPending}
                    style={{ ...entrada, cursor: "pointer", textAlign: "left" }}>
                    <b>Tirar a imagem de fundo</b>{" "}
                    <span style={{ color: "var(--txt3)" }}>o PNG anexado a este componente</span>
                  </button>
                )}
              </Secao>

              <Secao titulo="Ligações (tabela)" chave="componente-terminais"
                     resumo={terminais.data ? `${terminais.data.length}` : undefined}>
                <TerminaisDoComponente
                  projetoId={projetoId} diagramaId={diagramaId} componenteId={componente.id}
                  cor={componente.cor} desenhoInternoId={componente.desenhoInternoId}
                  aoGravar={aoMudar}
                />
              </Secao>

              <Secao titulo="Incluir ligações deste componente" chave="componente-incluir">
                <IncluirLigacoesDoComponente
                  projetoId={projetoId} diagramaId={diagramaId} componenteId={componente.id}
                  codigo={bloco?.codigo ?? ""} aoMudar={fechando}
                />
              </Secao>

              <Secao titulo="Desagrupar — separar em caixas" chave="componente-desagrupar">
                <Desagrupar
                  projetoId={projetoId} diagramaId={diagramaId} componenteId={componente.id}
                  codigo={bloco?.codigo ?? ""} aoMudar={fechando}
                />
              </Secao>

              {repetidos.length > 0 && (
                <Secao titulo="Remover pino repetido" chave="componente-repetido" aberta
                       resumo={`${new Set(repetidos.map((t) => t.pinoutId)).size} pino(s)`}>
                  <RemoverPinoRepetido
                    projetoId={projetoId} diagramaId={diagramaId} componenteId={componente.id}
                    repetidos={repetidos} aoMudar={fechando}
                  />
                </Secao>
              )}

              <Secao titulo="Transformar em interno" chave="componente-interno">
                <TransformarEmInterno
                  projetoId={projetoId} diagramaId={diagramaId} componenteId={componente.id}
                  codigo={bloco?.codigo ?? ""} aoMudar={fechando}
                />
              </Secao>

              <Secao titulo="O que ainda não tem tela" chave="componente-sem-tela">
                <span style={{ fontSize: 10.5, lineHeight: 1.6, color: "var(--txt3)" }}>
                  Do menu do sistema antigo: anexar PNG ao componente, incluir ligação interna (a
                  rota existe; é operação de um diagrama só em todo o acervo), e os atalhos para os
                  cadastros de conector e circuito, que vivem nas telas de cadastro.
                </span>
              </Secao>

              {/* Por último e fechado: remover não pode estar no caminho de quem só passa. */}
              <Secao titulo="Remover do diagrama" chave="componente-remover" perigo>
                <span style={{ fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
                  Exclusão lógica (RN-15): a caixa sai do desenho e vai para a lixeira do diagrama,
                  de onde dá para restaurar.
                </span>
                <button
                  onClick={() => {
                    excluir.mutate({ tipo: "componentes", id: componente.id }, {
                      onSuccess: () => fechando("Foi para a lixeira — dá para restaurar."),
                      onError: (e) => aoMudar(mensagem(e)),
                    })
                  }}
                  disabled={excluir.isPending}
                  style={{ ...entrada, cursor: "pointer", alignSelf: "flex-start",
                           color: "var(--erro)", fontWeight: 600 }}>
                  {excluir.isPending ? "Removendo…" : `Remover ${bloco?.codigo ?? "o componente"} do diagrama`}
                </button>
              </Secao>
            </div>
          </>
        )}
    </BarraLateral>
  )
}

const caixaDoComando: React.CSSProperties = {
  padding: "4px 7px", fontSize: 11.5, border: "1px solid var(--line)",
  borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
}

/**
 * Juntar nesta caixa as outras do mesmo componente — o "Agrupar" do menu do
 * legado, a partir da engrenagem.
 *
 * Só aparece quando há o que juntar: outra caixa do mesmo componente, **na mesma
 * coluna** (a seleção do legado também não atravessava de lado), e com desenho
 * próprio. Vêm marcadas as que estão marcadas no desenho; sem nenhuma marcada,
 * vêm todas.
 */
function JuntarCaixas({ projetoId, diagramaId, bloco, blocos, marcados, aoMudar }: {
  projetoId: number
  diagramaId: number
  bloco: BlocoDaMontagem
  blocos: BlocoDaMontagem[]
  marcados: number[]
  aoMudar: (recado: string) => void
}) {
  const outras = blocos.filter((b) =>
    b.componenteId === bloco.componenteId && b.diagramaComponenteId !== bloco.diagramaComponenteId
    && b.posicao === bloco.posicao && b.exibir)
  const [escolhidas, setEscolhidas] = useState<number[]>(() => {
    const jaMarcadas = outras.filter((b) => marcados.includes(b.diagramaComponenteId))
    return (jaMarcadas.length > 0 ? jaMarcadas : outras).map((b) => b.diagramaComponenteId)
  })
  const [conferindo, setConferindo] = useState(false)

  if (outras.length === 0) return null

  return (
    <div style={{ padding: "9px 11px", borderRadius: 8, display: "flex", flexDirection: "column", gap: 7,
                  background: "var(--acao-soft)", color: "var(--acao-on-soft)",
                  border: "1px solid var(--acao)" }}>
      <b style={{ fontSize: 12 }}>
        Há mais {outras.length} caixa{outras.length > 1 ? "s" : ""} do {bloco.codigo} nesta coluna
      </b>
      <span style={{ fontSize: 11, lineHeight: 1.5, opacity: 0.9 }}>
        Juntar traz os pinos delas para esta caixa, e as que ficam vazias somem. Nenhum pino se perde.
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {outras.map((b) => (
          <label key={b.diagramaComponenteId}
                 style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11 }}>
            <input type="checkbox" checked={escolhidas.includes(b.diagramaComponenteId)}
                   disabled={conferindo}
                   onChange={() => setEscolhidas((e) => e.includes(b.diagramaComponenteId)
                     ? e.filter((x) => x !== b.diagramaComponenteId) : [...e, b.diagramaComponenteId])} />
            <span>
              {b.codigo} · {b.terminais} ligação{b.terminais === 1 ? "" : "ões"}
              <span style={{ opacity: 0.7 }}> · ordem {b.ordem}</span>
              {marcados.includes(b.diagramaComponenteId) && <span style={{ opacity: 0.7 }}> · marcada</span>}
            </span>
          </label>
        ))}
      </div>
      {conferindo ? (
        <ConfirmarAgrupamento
          projetoId={projetoId} diagramaId={diagramaId}
          principal={bloco.diagramaComponenteId}
          ids={[bloco.diagramaComponenteId, ...escolhidas]}
          aoMudar={aoMudar}
          aoCancelar={() => setConferindo(false)}
        />
      ) : (
        <button onClick={() => setConferindo(true)} disabled={escolhidas.length === 0}
                style={{ ...caixaDoComando, cursor: "pointer", fontWeight: 600, alignSelf: "flex-start" }}>
          Juntar {escolhidas.length === 1 ? "1 caixa" : `${escolhidas.length} caixas`} nesta…
        </button>
      )}
    </div>
  )
}

/**
 * Remover pino repetido — o "Agrupar ligações" do legado.
 *
 * Trabalha **dentro de uma caixa só**: quando o mesmo pino ficou lançado duas
 * vezes, fica um e o outro é apagado de verdade (não é a lixeira). Só aparece
 * quando a caixa tem pino repetido, e só lista os repetidos — o nome antigo,
 * "Agrupar", levava quem queria juntar caixas a apagar pino.
 */
function RemoverPinoRepetido({ projetoId, diagramaId, componenteId, repetidos, aoMudar }: {
  projetoId: number
  diagramaId: number
  componenteId: number
  repetidos: TerminalDoComponente[]
  aoMudar: (recado: string) => void
}) {
  const remover = usarAgruparLigacoes(projetoId, diagramaId)
  const [marcados, setMarcados] = useState<number[]>([])

  return (
    <>
      <span style={{ fontSize: 11, color: "var(--txt3)", lineHeight: 1.55 }}>
        O mesmo pino está lançado mais de uma vez nesta caixa. Marque primeiro o que <b>fica</b> e
        depois os repetidos: estes são <b>apagados de vez</b> — não vão para a lixeira.
      </span>
      {repetidos.map((t) => (
        <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11 }}>
          <input type="checkbox" checked={marcados.includes(t.id)}
                 onChange={() => setMarcados((m) =>
                   m.includes(t.id) ? m.filter((x) => x !== t.id) : [...m, t.id])} />
          <b>pino {t.pino || "—"}</b>
          <span style={{ color: "var(--txt3)" }}>{t.codigoDoOutro} · {t.posicao}</span>
          {marcados[0] === t.id && (
            <span style={{ color: "var(--ok-on-soft)", fontSize: 10 }}>fica</span>
          )}
          {marcados.indexOf(t.id) > 0 && (
            <span style={{ color: "var(--erro)", fontSize: 10 }}>é apagado</span>
          )}
        </label>
      ))}
      <button
        disabled={marcados.length < 2 || remover.isPending}
        onClick={() => remover.mutate({ componenteId, ids: marcados }, {
          onSuccess: (r) => aoMudar(
            `${r.removidas} pino(s) repetido(s) apagado(s); ficou o ${r.terminalQueFicou}.`),
          onError: (e) => aoMudar(mensagem(e)),
        })}
        style={{ ...caixaDoComando, cursor: "pointer", color: "var(--erro)", fontWeight: 600,
                 alignSelf: "flex-start" }}>
        {remover.isPending ? "Apagando…"
          : marcados.length < 2 ? "Marque o que fica e o repetido"
          : `Apagar ${marcados.length - 1} repetido(s)`}
      </button>
    </>
  )
}

/** Incluir ligações a partir do pinout deste componente — o `DiagramaComponenteLigacoes` do legado. */
function IncluirLigacoesDoComponente({ projetoId, diagramaId, componenteId, codigo, aoMudar }: {
  projetoId: number
  diagramaId: number
  componenteId: number
  codigo: string
  aoMudar: (recado: string) => void
}) {
  const candidatos = usarCandidatosDoComponente(componenteId, true)
  const incluir = usarIncluirLigacoesDoComponente(projetoId, diagramaId)
  // As linhas marcadas, pela chave do par (pinout do escolhido + pinout deste).
  const [marcadas, setMarcadas] = useState<Map<string, LinhaMarcada>>(new Map())

  function alternarLinha(componenteId: number, l: PinoCandidato) {
    setMarcadas((m) => {
      const n = new Map(m)
      const chave = chaveDoPar(l)
      if (n.has(chave)) n.delete(chave)
      else n.set(chave, { pinoutId: l.pinoutId, ligacaoPinoutId: l.ligacaoPinoutId, componenteId })
      return n
    })
  }

  function alternarCandidato(c: CandidatoDoComponente) {
    const ligado = c.ligacoes.some((l) => marcadas.has(chaveDoPar(l)))
    setMarcadas((m) => {
      const n = new Map(m)
      for (const l of c.ligacoes) {
        if (ligado) n.delete(chaveDoPar(l))
        else n.set(chaveDoPar(l), { pinoutId: l.pinoutId, ligacaoPinoutId: l.ligacaoPinoutId, componenteId: c.componenteId })
      }
      return n
    })
  }

  return (
    <>
      <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.55 }}>
        Os pinos que <b>{codigo}</b> pode ganhar. É lista diferente da inclusão em lote do
        diagrama: aqui o motor parte do pinout deste componente, não do principal, e a
        ligação é de <b>{codigo}</b> com o escolhido — que entra ao lado dele, na mesma
        coluna. Aterramento e fusível: um de cada vez, como no sistema antigo.
      </span>
      {candidatos.isPending ? (
        <span style={{ fontSize: 11 }}>Carregando…</span>
      ) : (candidatos.data ?? []).length === 0 ? (
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>Nenhum pino a incluir.</span>
      ) : (
        <>
          <div style={{ maxHeight: 260, overflow: "auto", display: "flex",
                        flexDirection: "column", gap: 5 }}>
            {emOrdemDePinoPrincipal(candidatos.data ?? []).map((c) => (
              <div key={`${c.componenteId}-${c.ordem}`}
                   style={{ padding: "5px 7px", borderRadius: 6, background: "var(--surface2)" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                  <input type="checkbox"
                         checked={c.ligacoes.some((l) => marcadas.has(chaveDoPar(l)))}
                         onChange={() => alternarCandidato(c)} />
                  <NomeDoCandidato candidato={c} />
                </label>
                <div style={{ marginTop: 3, paddingLeft: 20 }}>
                  <PinosDoCandidato
                    ligacoes={c.ligacoes ?? []}
                    rotuloDoInicio={`Pino de ${codigo}`}
                    marcado={(l) => marcadas.has(chaveDoPar(l))}
                    aoAlternar={(l) => alternarLinha(c.componenteId, l)}
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            disabled={incluir.isPending || marcadas.size === 0}
            onClick={() => incluir.mutate(
              { componenteId, pares: [...marcadas.values()] },
              {
                onSuccess: (r) => { setMarcadas(new Map()); aoMudar(
                  `${r.ligacoesInseridas} ligação(ões) e ${r.componentesInseridos} componente(s) inseridos.`) },
                onError: (e) => aoMudar(mensagem(e)),
              })}
            style={{ ...caixaDoComando, cursor: "pointer", fontWeight: 600, alignSelf: "flex-start" }}>
            {incluir.isPending ? "Incluindo…" : `Incluir ${marcadas.size === 1 ? "a marcada" : `as ${marcadas.size} marcadas`}`}
          </button>
        </>
      )}
    </>
  )
}

/** Quebra o componente em vários, um por componente interno. **Não tem volta.** */
function TransformarEmInterno({ projetoId, diagramaId, componenteId, codigo, aoMudar }: {
  projetoId: number
  diagramaId: number
  componenteId: number
  codigo: string
  aoMudar: (recado: string) => void
}) {
  const interno = usarTransformarInterno(projetoId, diagramaId)
  return (
    <>
      <b style={{ fontSize: 11.5, color: "var(--erro)" }}>Irreversível.</b>
      <span style={{ fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.55 }}>
        Quebra <b>{codigo}</b> em vários componentes, um por componente interno achado nos
        pinouts das ligações. Insere e realoca — não tem desfazer.
      </span>
      <button
        disabled={interno.isPending}
        onClick={() => interno.mutate(componenteId, {
          onSuccess: (r) => aoMudar(
            `${r.componentesCriados} componente(s) criado(s), ${r.ligacoesRealocadas} ligação(ões) realocada(s).`),
          onError: (e) => aoMudar(mensagem(e)),
        })}
        style={{ ...caixaDoComando, cursor: "pointer", color: "var(--erro)", fontWeight: 600,
                 alignSelf: "flex-start" }}>
        {interno.isPending ? "Transformando…" : "Transformar mesmo assim"}
      </button>
    </>
  )
}

/**
 * O aviso de que o desenho está sendo refeito — depois de gravar, agrupar,
 * desagrupar ou do Recarregar.
 *
 * **Leve de propósito**: o desenho continua à vista, só esmaecido, com uma barra
 * correndo no topo. Recarregar leva de 1 a 10 s, e sem isso a tela parecia
 * parada; tapar tudo com um carregando seria pior, porque a pessoa perde o lugar.
 */
function Recarregando() {
  return (
    <div role="status" aria-live="polite"
         style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none",
                  background: "rgba(255,255,255,0.45)" }}>
      <style>{`
        @keyframes montagem-correndo { from { transform: translateX(-100%) } to { transform: translateX(250%) } }
        @media (prefers-reduced-motion: reduce) { .montagem-barra { animation-duration: 4s !important } }
      `}</style>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, overflow: "hidden",
                    background: "var(--acao-soft)" }}>
        <div className="montagem-barra"
             style={{ width: "40%", height: "100%", background: "var(--acao)",
                      animation: "montagem-correndo 1.1s ease-in-out infinite" }} />
      </div>
      <span style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
                     padding: "4px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                     background: "var(--surface)", color: "var(--txt2)",
                     border: "1px solid var(--line2)", boxShadow: "var(--sombra)" }}>
        Atualizando o desenho…
      </span>
    </div>
  )
}

/**
 * O traço de um bloco, higienizado.
 *
 * Usa o mesmo `higienizarSvg` do visor do pinout — o motor concatena texto do
 * banco sem escapar, e SVG embutido é markup da página. Não usa o `DesenhoSvg`
 * porque aqui o tamanho é do bloco, não do quadro, e há dezenas deles.
 */
function Traco({ svg, largura, zoom, rotulo, esticar }: {
  svg: string
  largura: number
  zoom: number
  rotulo: string
  esticar?: boolean
}) {
  const limpo = useMemo(() => {
    if (!svg) return null
    try {
      const el = higienizarSvg(svg).elemento
      // A barra do principal estica na altura de proposito: e uma barra, e a
      // pagina antiga faz o mesmo por JS. Sem isto o SVG mantem a proporcao e
      // sobra vazio embaixo.
      if (esticar) el.setAttribute("preserveAspectRatio", "none")
      return el.outerHTML
    } catch { return null }
  }, [svg, esticar])

  if (!limpo) {
    return <span style={{ fontSize: 10.5, color: "var(--txt3)", padding: 6, display: "block" }}>
      sem traço
    </span>
  }

  // O desenho tem viewBox, entao `width: 100%` escala em vez de cortar: a coluna
  // manda, e o tamanho natural vezes o zoom e so o teto. Sem isso o bloco de 386
  // px ficava cortado numa coluna de 290.
  return (
    <div
      role="img"
      aria-label={rotulo}
      className={esticar ? "traco-principal" : "traco-bloco"}
      style={{
        width: "100%",
        maxWidth: largura > 0 && !esticar ? `${largura * zoom}px` : undefined,
        ...(esticar ? { height: "100%", minHeight: 120 } : {}),
      }}
      dangerouslySetInnerHTML={{ __html: limpo }}
    />
  )
}

/**
 * O SVG de dentro obedece ao quadro.
 *
 * O principal e uma barra de altura fixa 100 no motor, esticada pela pagina
 * antiga com JS (`tamanho()`); aqui o CSS estica, e `preserveAspectRatio: none`
 * e o certo justamente por ser barra — deformar na altura e o efeito desejado.
 */
const CSS_DOS_BLOCOS = `
.traco-bloco svg { display: block; width: 100%; height: auto; }
.traco-principal { display: flex; }
.traco-principal svg { display: block; width: 100%; height: 100%; }
`

/** Tira as tags `<style>` de fora e devolve só o CSS de dentro. */
function estiloDeDentro(estilos: string): string {
  return estilos.replace(/<\/?style[^>]*>/gi, "")
}

/**
 * A tela cheia do navegador aplicada só à montagem. O Esc sai (quem cuida é o
 * navegador) e o estado acompanha: sair pelo Esc também troca o rótulo do botão.
 * Os painéis de componente e de ferramentas são filhos do quadro, então abrem
 * dentro da tela cheia.
 */
function usarTelaCheia(alvo: React.RefObject<HTMLElement>) {
  const [ligada, setLigada] = useState(false)
  useEffect(() => {
    const mudou = () => setLigada(document.fullscreenElement != null && document.fullscreenElement === alvo.current)
    document.addEventListener("fullscreenchange", mudou)
    return () => document.removeEventListener("fullscreenchange", mudou)
  }, [alvo])
  const disponivel = typeof document !== "undefined" && document.fullscreenEnabled === true
  function alternar() {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void alvo.current?.requestFullscreen()
  }
  return { ligada, disponivel, alternar }
}
